import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  Inject,
  PLATFORM_ID,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SearhService } from '../../../../services/search.service';
import { UrlSlugifyService } from '../../../../services/url-slugify';

@Component({
  selector: 'app-searchpage',
  templateUrl: './searchpage.component.html',
  styleUrl: './searchpage.component.scss'
})
export class SearchpageComponent
  implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mobileInput') mobileInput!: ElementRef<HTMLInputElement>;

  isMobile = false;
  isAr = false;

  suggestions: string[] = [];
  placeholderText = '';
  translatedText = '';

  searchkey = '';

  private intervalId: any;
  private index = 0;
  private charIndex = 0;
  private typingForward = true;
  private isBrowser: boolean;

  constructor(
    private router: Router,
    private deviceService: DeviceDetectorService,
    private translate: TranslateService,
    private _SearchService: SearhService,
    private _slugify: UrlSlugifyService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  // ================= INIT =================
  ngOnInit(): void {

    this.isMobile = this.deviceService.isMobileDevice();

    if (this.isBrowser) {
      this.isAr = localStorage.getItem('language') === 'ar';
    }

    this.suggestions = this.isAr
      ? [
        'اكتب دراي فود...',
        'اكتب فرشاة بخار مانجو...',
        'اكتب اسم الماركة...',
        'اكتب اسم الدواء...',
        'اكتب منتجات قطط معقمة...',
        'اكتب منتجات كلب بالغة...'
      ]
      : [
        'Try dry food...',
        'Try product name...',
        'Try brand name...',
        'Try medicine...',
        'Try sterilized cat products...',
        'Try adult dog products...'
      ];

    this.placeholderText = this.suggestions[0];
    this.translatedText = this.translate.instant('What can I help with?');

    if (this.isBrowser) {
      this.startRotation();
    }
  }

  // ================= VIEW =================
  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    setTimeout(() => {
      this.mobileInput?.nativeElement?.focus();
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // ================= PLACEHOLDER ROTATION =================
  startRotation(): void {
    this.intervalId = setInterval(() => {
      if (this.typingForward) {
        this.charIndex++;
        if (this.charIndex === this.suggestions[this.index].length) {
          this.typingForward = false;
        }
      } else {
        this.charIndex--;
        if (this.charIndex === 0) {
          this.typingForward = true;
          this.index = (this.index + 1) % this.suggestions.length;
        }
      }

      this.placeholderText =
        this.suggestions[this.index].substring(0, this.charIndex);
    }, 100);
  }

  stopRotation(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.placeholderText = '';
  }

  // ================= SEARCH LOGIC =================
  testSearchKey() {
    const categoryMap: Record<string, number> = {
      'soft food': 0, 'طعام رطب - باوتش': 0,
      'soft food can': 1, 'طعام رطب - علبة': 1,
      'dry food': 2, 'طعام جاف': 2,
      'bird food': 3, 'طعام الطيور': 3,
      'medicine': 4, 'أدوية': 4,
      'supplement': 5, 'مكملات غذائية': 5,
      'accessories': 6, 'إكسسوارات': 6,
      'toy': 7, 'ألعاب': 7,
      'grooming': 8, 'أدوات عناية': 8,
      'house': 9, 'بيوت/منازل': 9,
      'care': 10, 'رعاية': 10,
      'treats': 11, 'مكافآت': 11,
      'training pads': 12, 'أدوات تدريب': 12,
      'bags': 13, 'حقائب': 13,
      'cleaning tools': 14, 'أدوات تنظيف': 14,
      'litter': 15, 'رمل قطط': 15,
    };

    const petMap: Record<string, number> = {
      'cats': 0, 'قطط': 0,
      'dogs': 1, 'كلاب': 1,
      'birds': 2, 'طيور': 2,
      'other': 3, 'اخري': 3,
      'cats&dogs': 4, 'قطط و كلاب': 4,
    };

    const text = this.searchkey.toLowerCase().trim();

    const findMatch = (map: Record<string, number>, input: string) => {
      for (const key in map) {
        if (key.toLowerCase().includes(input)) {
          return map[key];
        }
      }
      return undefined;
    };

    const petMatch = findMatch(petMap, text);
    const catMatch = findMatch(categoryMap, text);

    if (petMatch !== undefined) {
      this.byPet('pet', petMatch);
    } else if (catMatch !== undefined) {
      this.byCategory(this.searchkey, catMatch);
    } else {
      this.search(text);
    }
  }

  // ================= API SEARCH =================
  search(key: string): void {
    this._SearchService.searchByNameAndWarehouse(key, 1).subscribe({
      next: (data) => {
        if (data.length === 1) {
          this.productDetails(data[0]);
        } else if (data.length > 1) {
          const ids = data.map((item: any) => item.id);
          this.router.navigate(['/search-result'], {
            queryParams: { ids: ids.join(',') }
          });
        } else {
          this.router.navigate(['/no-result']);
        }
      },
      error: (error) => console.error(error),
    });
  }

  // ================= NAVIGATION =================
  productDetails(product: any): void {
    this.router.navigate([
      '/product',
      this._slugify.slugify(product.name_en),
      product.id
    ]);
  }

  byPet(pet: any, petId: any): void {
    this.router.navigate(['/pet', pet, petId]);
  }

  byCategory(category: any, categoryId: any): void {
    this.router.navigate([
      '/category',
      this._slugify.slugify(category),
      categoryId
    ]);
  }
}
